import React from "react";
import Footer from "../Footer";
import Navbar from "../Navbar";
import SEO from "../SEO";
import { FaUndo, FaBackspace, FaCoins, FaPaperPlane, FaShieldAlt } from "react-icons/fa";

const CancellationRefundPolicy: React.FC = () => {
  const policySections = [
    {
      title: "1. Fair Cancellation Window",
      icon: <FaBackspace />,
      content: `At ${import.meta.env.VITE_COMPANY_NAME}, we understand that circumstances change. If you change your mind before the loan is disbursed, you can cancel your application instantly without any fees. Even after disbursement, we offer a 24-hour review period for returns.`,
    },
    {
      title: "2. Transparent Refunds",
      icon: <FaUndo />,
      content: `Any excess payments or technical errors are reconciled with precision. Overpayments are typically refunded back to your original payment source within 5–7 business days after verification.`,
    },
    {
      title: "3. Responsible Timelines",
      icon: <FaPaperPlane />,
      list: [
        "Reconciliation: Every refund undergoes a detailed internal review to ensure accuracy.",
        "Notifications: You will receive real-time updates via SMS/Email once a refund is initiated.",
        "Bank Processing: Final reflection in your account depends on your bank's standard processing cycles.",
      ],
    },
    {
      title: "4. Policy Constants",
      icon: <FaShieldAlt />,
      list: [
        "Time Limits: Loans cannot be cancelled beyond 24 hours of successful credit.",
        "Fees: Once a loan is active, upfront processing fees or interest already accrued are typically non-refundable.",
        "Error Resolution: Duplicate transactions are prioritized and handled manually by our finance team.",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white text-text-primary px-0 mx-0 overflow-x-hidden font-sans selection:bg-secondary/10 selection:text-secondary">
      <SEO
        title="Cancellation & Refund"
        description={`Our transparent cancellation and refund guidelines. At ${import.meta.env.VITE_COMPANY_NAME}, we ensure your peace of mind with fair windows and clear rules.`}
        keywords={`cancellation policy, refund policy, fintech transparency, loan support`}
      />
      
      <div className="fixed inset-0 pointer-events-none z-0 bg-gradient-to-b from-white via-gray-50/30 to-white" />
      
      <div className="relative z-50">
        <Navbar />
      </div>

      <main className="relative z-10 pt-44 pb-20 md:pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          
          <header className="mb-20 text-center">
             <div className="inline-flex items-center gap-3 px-6 py-2 bg-white border border-secondary/20 shadow-xl rounded-full mb-10 transition-transform hover:scale-105">
                <div className="w-2 h-2 bg-secondary rounded-full animate-pulse" />
                <span className="text-[10px] font-black tracking-[0.3em] text-text-secondary uppercase">Honest Terms</span>
              </div>
            <h1 className="text-4xl md:text-8xl font-black tracking-tighter mb-10 text-text-primary leading-[0.9]">
              Fair <br />
              <span className="text-secondary">Reversals.</span>
            </h1>
            <p className="text-text-secondary text-lg md:text-2xl font-medium leading-relaxed max-w-2xl mx-auto">
              We believe in flexibility. Our cancellation and refund policies are designed to be as clear and supportive as our lending process.
            </p>
          </header>

          <div className="space-y-12">
            {policySections.map((section, idx) => (
              <div key={idx} className="bg-white p-10 md:p-14 rounded-[3rem] border border-gray-100 shadow-xl relative overflow-hidden transition-all hover:shadow-2xl">
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center text-primary text-3xl shrink-0">
                    {section.icon}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl md:text-4xl font-black mb-6 text-text-primary tracking-tight">
                      {section.title}
                    </h2>
                    {section.content && (
                      <p className="text-text-secondary text-lg md:text-xl leading-relaxed font-medium mb-6">
                        {section.content}
                      </p>
                    )}
                    {section.list && (
                      <ul className="space-y-4 pt-4">
                        {section.list.map((li, i) => (
                          <li key={i} className="flex items-start gap-4 text-text-secondary font-medium text-lg leading-relaxed">
                            <div className="w-2 h-2 bg-secondary mt-2.5 rounded-full shrink-0" />
                            <span>{li}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Regulatory Note */}
            <div className="bg-primary text-white p-12 md:p-20 rounded-[3rem] shadow-2xl relative overflow-hidden text-center">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-[80px]" />
                <h3 className="text-2xl font-black mb-6 flex items-center justify-center gap-3">
                  <FaCoins className="text-secondary text-lg" />
                  Institution Guarantee
                </h3>
                <p className="text-white/80 text-lg md:text-xl leading-relaxed font-medium max-w-2xl mx-auto">
                  Every reversal request is handled with institutional priority. We guarantee that all valid refund or cancellation claims are processed within the strict regulatory timelines of ${import.meta.env.VITE_COMPANY_NBFC}.
                </p>
                <div className="mt-8 font-black text-xs uppercase tracking-[0.3em] text-secondary">Registered NBFC accountability</div>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CancellationRefundPolicy;
